/**
 * Centralized Firestore collection name constants.
 *
 * Every Firestore collection referenced in the codebase should use one of these
 * constants instead of a hardcoded string. This makes collection renames and
 * platform forks a single-line change.
 *
 * Shared between src/ (client) and functions/src/ (server) via path alias or
 * direct import.
 */

// ── Root-level collections ───────────────────────────────────────────

export const C_OPS = '_ops';
export const C_ADMIN_USERS = 'admin_users';
export const C_AFFILIATE_PHYSICIANS = 'affiliate_physicians';
export const C_ANALYSIS_ERRORS = 'analysis_errors';
export const C_API_USAGE_LOGS = 'api_usage_logs';
export const C_CALIBRATION_CASES = 'calibration_cases';
export const C_CALIBRATION_CONVERGENCE = 'calibration_convergence';
export const C_CALIBRATION_RUNS = 'calibration_runs';
export const C_CAMPAIGNS = 'campaigns';
export const C_DATAOPS_AUDIT_LOG = 'dataops_audit_log';
export const C_DATAOPS_BREACH_EVENTS = 'dataops_breach_events';
export const C_DATAOPS_COMPLIANCE = 'dataops_compliance';
export const C_CONSENT_DOCUMENTS = 'consent_documents';
export const C_DATAOPS_CONSENT = 'dataops_consent';
export const C_DATAOPS_MINOR_DETECTIONS = 'dataops_minor_detections';
export const C_DATAOPS_REQUESTS = 'dataops_requests';
export const C_DATAOPS_RETENTION_JOBS = 'dataops_retention_jobs';
export const C_DATAOPS_RETENTION_POLICIES = 'dataops_retention_policies';
export const C_DATAOPS_THIRD_PARTY_SHARES = 'dataops_third_party_shares';
export const C_DOC_BRANDS = 'doc_brands';
export const C_DOC_CATEGORIES = 'doc_categories';
export const C_DOC_OWNERS = 'doc_owners';
export const C_DOC_SESSIONS = 'doc_sessions';
export const C_DOC_TEMPLATES = 'doc_templates';
export const C_EVENT_BUS = 'event_bus';
export const C_EVENT_CONSUMERS = 'event_consumers';
export const C_FASHION_PLATES = 'fashion_plates';
export const C_FORMULARY = 'formulary';
export const C_PII_REDACTION_MAPS = 'pii_redaction_maps';
export const C_FUNNEL_EVENTS = 'funnel_events';
export const C_GCHAT_CONVERSATIONS = 'gchat_conversations';
export const C_GENERATION_JOBS = 'generation_jobs';
export const C_ID_MAPPINGS = 'id_mappings';
export const C_INTAKE_PENDING_SUBMISSIONS = 'intake_pending_submissions';
export const C_LAB_EXTRACTIONS = 'lab_extractions';
/**
 * sessions/{sessionId}/safetyProfile/current
 * Owner: Nurse bone (NRS.08, firestoreAdapters.ts)
 * Purpose: CLINICAL SAFETY PROFILE — medications, allergies, conditions, pregnancy status.
 * Written by Nurse. Read by PRO.01 (inputAssembly.ts) for conservative defaults.
 * NOT conversation state. See patient_journeys/{patientUid}/session_state/current for that.
 * Spec: HLX-NRS.08, HLX-PRO.01 | Dictionary: _ops/firestore-data-dictionary.md
 */
export const C_SESSIONS = 'sessions';
export const SC_SAFETY_PROFILE = 'safetyProfile';
export const C_LAB_SESSIONS = 'lab_sessions';
export const C_MAIL_QUEUE = 'mail_queue';
export const C_NURSE_CONFIGS = 'nurse_configs';
export const C_ORDERS = 'orders';
export const C_PATIENT_ANALYSES = 'patient_analyses';
export const C_PATIENT_CONSENTS = 'patient_consents';
export const C_PATIENT_EVENTS = 'patient_events';
/**
 * patient_journeys/{patientUid}/session_state/current
 * Owner: Enrichment pipeline (PRO.09 late reveal, sessionState.ts)
 * Purpose: CONVERSATION STATE — modifiers, late reveals, session phase tracking.
 * NOT the clinical safety profile. See sessions/{sessionId}/safetyProfile/current for that.
 * Spec: HLX-PRO.09 | Dictionary: _ops/firestore-data-dictionary.md
 */
export const C_PATIENT_JOURNEYS = 'patient_journeys';
export const C_PATIENT_PROGRAMS = 'patient_programs';
export const C_PATIENTS = 'patients';
export const C_PAYMENT_EVENTS = 'payment_events';
export const C_PHI_GATE_AUDIT = 'phi_gate_audit';
export const C_PLATFORM = 'platform';
export const C_BRANDS = 'brands';
export const C_PROMISE_KEEPER = 'promise_keeper';
export const C_REGULATORY_ALERTS = 'regulatory_alerts';
export const C_REGULATORY_CAMPAIGNS = 'regulatory_campaigns';
export const C_REGULATORY_POLICIES = 'regulatory_policies';
export const C_SAFETY = 'safety';
export const C_STAN_DIRECTIVES = 'stan_directives';
export const C_STORIES = 'stories';
export const C_STORM_FORECASTER_HISTORY = 'storm_forecaster_history';
export const C_STORM_INSPECTIONS = 'storm_inspections';
export const C_STORM_REPORTS = 'storm_reports';
export const C_USER_PROFILES = 'user_profiles';
export const C_CONFIG_VIOLATIONS = 'config_violations';
export const C_FINGERPRINT_ANOMALIES = 'fingerprint_anomalies';
export const C_PENDING_BUILD_AUTH = 'pending_build_auth';
export const C_WEBHOOK_IDEMPOTENCY = 'webhook_idempotency';
export const C_DAILY_AUDITS = 'daily_audits';
export const C_FORECAST_PLANS = 'forecast_plans';
export const C_JOURNEY_CONFIG = 'journey_config';
export const C_SPEC_COVERAGE = 'spec_coverage';

// ── Eddie Dispute Resolution collections ────────────────────────────

export const C_EDDIE_VERDICTS = 'eddie_verdicts';
export const C_EDDIE_DISPUTES = 'eddie_disputes';

// ── Patient Critical Modifiers + Module Prompts ─────────────────────

export const C_PATIENT_CRITICAL_MODIFIERS = 'patient_critical_modifiers';
export const C_MODULE_PROMPTS = 'module_prompts';

// ── PJE (Patient Journey Engine) collections ─────────────────────────

export const C_ARCHETYPE_TEMPLATES = 'archetype_templates';

export const C_PJE_GENERATED_PANELS = 'pje_generated_panels';
export const C_PJE_GROUND_TRUTH = 'pje_ground_truth';
export const C_PJE_LOAD_RUNS = 'pje_load_runs';
export const C_PJE_MODIFIER_FLOWS = 'pje_modifier_flows';
export const C_PJE_PANEL_COMPARISONS = 'pje_panel_comparisons';
export const C_PJE_PANELS = 'pje_panels';
export const C_PJE_PERSONAS = 'pje_personas';
export const C_PJE_PROMPT_ANALYSES = 'pje_prompt_analyses';
export const C_PJE_PROMPT_CONFIGS = 'pje_prompt_configs';
export const C_PJE_REGISTER_EVALUATIONS = 'pje_register_evaluations';
export const C_PJE_SUITE_STATS = 'pje_suite_stats';
export const C_PJE_TEST_RUNS = 'pje_test_runs';
export const C_PJE_TEST_SUITES = 'pje_test_suites';
export const C_PJE_TRAIT_PROFILES = 'pje_trait_profiles';

// ── Subcollection names ──────────────────────────────────────────────

export const SC_ACTIVE = 'active';
export const SC_CALIBRATION_SESSIONS = 'calibration_sessions';
export const SC_CLINICAL_ANALYSIS = 'clinicalAnalysis';
export const SC_DOCUMENTS = 'documents';
export const SC_FINANCIAL_SNAPSHOTS = 'financial_snapshots';
export const SC_METRICS = 'metrics';
export const SC_NOTIFICATIONS = 'notifications';
export const SC_ORDERS = 'orders';
export const SC_QBO_CONFIG = 'qbo_config';
export const SC_REFILL_TIMERS = 'refill_timers';
export const SC_STRIPE_CONFIG = 'stripe_config';
export const SC_SYNC_RULES = 'sync_rules';
export const SC_CALIBRATION = 'calibration';
export const SC_DIVERGENCE_SUMMARIES = 'divergence_summaries';
export const SC_LAUNCH_GATE = 'launch_gate';
export const SC_PJE_CONFIG = 'pje_config';
export const SC_TEST_RUNS = 'test_runs';
export const SC_STORM_ALERTS_LOG = 'log';
export const SC_MODIFIERS = 'modifiers';
export const SC_SESSION_STATE = 'session_state';

// ── Platform nested document names ──────────────────────────────────

export const DOC_CALIBRATION = 'calibration';
export const SC_CLINICAL_CALIBRATION = 'clinical_calibration';
export const SC_PROMPTS = 'prompts';

// ── Nested _ops paths ────────────────────────────────────────────────

export const OPS_ALERTS_ACTIVE = 'alerts/active';
export const OPS_COSTS_DAILY = 'costs/daily';
export const OPS_COSTS_BUDGETS_CURRENT = 'costs/budgets/current';
export const OPS_CONFIG_PRICING_CURRENT = 'config/pricing/current';
export const OPS_CONFIG_PLAYBOOK_CURRENT = 'config/playbook/current';
export const OPS_COMPLIANCE_TOS_CURRENT = 'compliance/tos-controls/current';
export const OPS_DEBT_FINDINGS = 'debt/findings';
export const OPS_DEBT_SURVEYS = 'debt/surveys';
export const OPS_DEBT_SESSIONS = 'debt/sessions';
export const OPS_DEBT_BRIEFINGS = 'debt/briefings';
export const OPS_DEPS_SNAPSHOTS_CURRENT = 'deps/snapshots/current';
export const OPS_BUILDS_SNAPSHOTS_CURRENT = 'builds/snapshots/current';
export const OPS_HEALTH_WEEKLY = 'health/weekly';
export const OPS_HEALTH_MONTHLY = 'health/monthly';
export const OPS_REMEDIATION_LOG = 'remediation/log';
export const OPS_REMEDIATION_RULES = 'remediation/rules';
export const OPS_UNSENT_MESSAGES_QUEUE = 'unsent_messages/queue';

// ── _ops document names ──────────────────────────────────────────────

export const OPS_DOC_GCHAT_HEARTBEAT = 'gchat_heartbeat';
export const OPS_DOC_SWARM_MONITOR_STATE = 'swarm_monitor_state';
export const OPS_DOC_STORM_HEARTBEAT = 'storm_heartbeat';
export const OPS_DOC_NOTIFY_HEALTH = 'notify_health';
export const OPS_DOC_NOTIFY_HMAC_SECRETS = 'notify_hmac_secrets';
export const OPS_DOC_SMS_CONFIG = 'sms_config';
export const OPS_DOC_STORM_ALERTS = 'storm_alerts';

// ── Formulary/Safety nested paths ────────────────────────────────────

export const FORMULARY_DATA_PRODUCTS = 'data';       // formulary/data/products
export const SC_PRODUCTS = 'products';
export const SC_BUNDLES = 'bundles';
export const SC_RULES = 'rules';
export const SC_MESSAGES = 'messages';
export const SC_STOCK_CASES = 'stock_cases';
export const SC_GENERIC_FORMULARY_MAP = 'generic_formulary_map';

// ── _ops additional paths ───────────────────────────────────────────

export const OPS_LINGUISTIC_BASELINE = 'linguistic_baseline';
export const OPS_HEALTH_DAILY = 'health/daily';
export const OPS_HEALTH_QUARTERLY = 'health/quarterly';
export const OPS_COSTS_MONTHLY = 'costs/monthly';
export const OPS_DEPS_SCANS = 'deps/scans';
export const OPS_BUILDS_HISTORY = 'builds/history';
export const OPS_ALERTS_HISTORY = 'alerts/history';
export const OPS_SMS_RATE_LIMITS = 'sms_rate_limits';

export const C_FAILED_JOURNEY_LINKS = 'failed_journey_links';

// ── Unit Validation (HLX-CAP.06) ─────────────────────────────────────
export const C_UNIT_DATABASE = 'unit_database';

// ── Lab Upload (CAP.01 D#6) ─────────────────────────────────────────
export const C_LAB_UPLOADS = 'lab_uploads';
export const C_UPLOAD_EVENTS = 'upload_events';

// ── Model Degradation Performance Monitoring ────────────────────────
export const OPS_MODEL_DEGRADATION_PERF_LOGS = 'model_degradation/performance_logs';
export const OPS_MODEL_DEGRADATION_PERF_SNAPSHOTS = 'model_degradation/performance_snapshots';

// ── PHI Temp File Metadata (CAP.03) ─────────────────────────────────
export const C_TEMP_FILE_METADATA = 'temp_file_metadata';
export const C_PHI_ANOMALIES = 'phi_anomalies';

// ── Conversation Audit (HLX-C13 flagging) ───────────────────────────
export const C_CONVERSATION_AUDIT_QUEUE = 'conversation_audit_queue';
export const C_CONVERSATION_AUDIT_FEEDBACK = 'conversation_audit_feedback';

// ── Test Results (storm scheduler test outcomes) ────────────────────
export const C_TEST_RESULTS = 'test_results';

// ── Confidence Pyramid / A/B Testing ─────────────────────────────────
export const C_MESSAGE_TIERS = 'message_tiers';
export const C_AB_TESTING_PRINCIPLES = 'ab_testing_principles';

// ── NAD / Sermorelin / B12 Messages ──────────────────────────────────
export const C_NAD_SERMORELIN_B12_MESSAGES = 'nad_sermorelin_b12_messages';

// ── Captain Results (CAP.17 Downstream Handoff) ─────────────────────
export const C_CAPTAIN_RESULTS = 'captain_results';

// ── Lab Date Validation (HLX-CAP.12) ──────────────────────────────
export const C_DATE_VALIDATION_AUDIT = 'date_validation_audit';
export const C_PHI_VIEW_AUDIT = 'phi_view_audit';
export const C_DATE_VALIDATION_REVIEW_QUEUE = 'date_validation_review_queue';

// ── Analyst Review Handoff (HLX-XMO.09) ────────────────────────────
/** xmod_analyst_reviews/{reviewId} — Analyst review lifecycle documents */
export const C_XMOD_ANALYST_REVIEWS = 'xmod_analyst_reviews';
/** captain_results/{sessionId}/corrections/{correctionId} — Analyst corrections subcollection */
export const SC_CAPTAIN_RESULT_CORRECTIONS = 'corrections';
/** patient_notifications — In-app patient notifications */
export const C_PATIENT_NOTIFICATIONS = 'patient_notifications';

// ── Content Moderation (HLX-XMO.11) ─────────────────────────────────
/** xmod_content_flags/{uploadId} — ContentFlag audit documents (XMO.11 D#3 c5) */
export const C_XMOD_CONTENT_FLAGS = 'xmod_content_flags';
